import { useEffect, useState } from 'react';
import type { PreloadedQuery } from 'react-relay';
import type { OperationType } from 'relay-runtime';

/**
 * Returns the most recent PreloadedQuery whose network fetch has resolved,
 * holding the previously-ready reference until the new one completes. Prevents
 * usePreloadedQuery from suspending the consumer (and flashing its parent
 * skeleton) when route-resource variables change. `useSyncExternalStore` would
 * be the idiomatic adapter, but JFE restricts React 18 APIs — revisit then.
 */
export function useReadyQueryReference<TQuery extends OperationType>(
	current: PreloadedQuery<TQuery> | null,
): PreloadedQuery<TQuery> | null {
	const [readyRef, setReadyRef] = useState<PreloadedQuery<TQuery> | null>(current);

	// No source means data is already in store, set the state with the current ref
	if (current && !current.source && current !== readyRef) {
		setReadyRef(current);
	}

	useEffect(() => {
		// Do nothing if there's no candidate, or if it's already the one we're rendering
		if (!current?.source || current === readyRef) {
			return;
		}

		// Promote on error too so the consumer's error boundary surfaces it.
		const promote = () => setReadyRef(current);
		const subscription = current.source.subscribe({ complete: promote, error: promote });
		return () => subscription.unsubscribe();
	}, [current, readyRef]);

	return readyRef;
}
